/**
 * @fileoverview Update-target-stage mutation hook.
 * @description Wraps PATCH /sales/targets/:id/stage in a TanStack mutation, invalidating that
 * tenant's target query on success.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { TARGETS_QUERY_KEY } from '@/features/sales/hooks/useTargets'
import { updateTargetStage } from '@/features/sales/services/targets.service'

import type { UpdateStageRequest } from '@/features/sales/services/targets.service'

/**
 * @description Updates a target's sales stage (owner-only; Lost/On Hold require a reason) and
 * refreshes the owning tenant's target query.
 * @param {string} tenantId - The tenant the target belongs to, for query invalidation.
 * @returns {ReturnType<typeof useMutation<Awaited<ReturnType<typeof updateTargetStage>>, unknown, {
 * targetId: string; input: UpdateStageRequest }>>} The mutation object (mutate with `{ targetId,
 * input }`, isPending, ...).
 */
export function useUpdateTargetStage(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ targetId, input }: { targetId: string; input: UpdateStageRequest }) =>
      updateTargetStage(targetId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...TARGETS_QUERY_KEY, 'byTenant', tenantId] })
    },
  })
}
